import { getGameAuth, selectGameServices } from '../firebase';

type Unsubscribe = () => void;

export const MAX_ROOM_PLAYERS = 8;
export const ROOM_INACTIVITY_MS = 15 * 60 * 1000;
export const MAX_ROOM_LIFETIME_MS = 30 * 60 * 1000;
export const GLOBAL_ACTIVE_ROOM_CAP = 100;

export type RoomStatus = 'lobby' | 'play' | 'closed';

export interface RoomPlayer {
  id: string;
  seat: number;
  name: string;
  pile: number[];
  guideOpen: boolean;
  microphoneReady: boolean;
  retainAudio: boolean | null;
}

export interface FppRoom {
  code: string;
  createdAtMs: number;
  lastActivityAtMs: number;
  status: RoomStatus;
  hostId: string;
  center: number[];
  players: RoomPlayer[];
  lastClaim: { playerId: string; word: string; atMs: number } | null;
  retainAudio: boolean | null;
}

export interface PeerSignalSnapshot {
  signal: {
    offer?: RTCSessionDescriptionInit;
    answer?: RTCSessionDescriptionInit;
    sessionId?: string;
    answerSessionId?: string;
    bNonce?: string;
  };
  candidates: Array<{
    id: string;
    from: string;
    sessionId: string;
    candidate: RTCIceCandidateInit;
  }>;
}

export type PeerSignalMutation =
  | { kind: 'nonce'; nonce: string }
  | { kind: 'offer'; sessionId: string; description: RTCSessionDescriptionInit }
  | { kind: 'answer'; sessionId: string; description: RTCSessionDescriptionInit }
  | { kind: 'candidate'; sessionId: string; candidate: RTCIceCandidateInit };

export type RoomSyncState = 'live' | 'reconnecting' | 'lost' | 'ended';

export class RoomError extends Error {
  constructor(
    readonly code:
      | 'full'
      | 'closed'
      | 'expired'
      | 'inactive'
      | 'cap'
      | 'started'
      | 'too_late'
      | 'mismatch'
      | 'auth'
      | 'missing'
      | 'not_in_room'
      | 'consent'
      | 'unavailable'
      | 'timeout',
    message: string,
  ) {
    super(message);
    this.name = 'RoomError';
  }
}

export function roomShareUrl(code: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://raajadharma.org';
  return `${origin}/products/tools/game?room=${code}`;
}

export function parseRoomCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return code.length === 6 ? code : null;
}

export function roomExpiry(room: FppRoom, now = Date.now()): 'expired' | 'inactive' | 'closed' | null {
  if (room.status === 'closed') return 'closed';
  if (now - room.createdAtMs > MAX_ROOM_LIFETIME_MS) return 'expired';
  if (room.status === 'play' && now - room.lastActivityAtMs > ROOM_INACTIVITY_MS) return 'inactive';
  return null;
}

export function audioRetentionConsensus(room: FppRoom): boolean | null {
  if (room.players.length === 0 || room.players.some((player) => player.retainAudio === null)) {
    return null;
  }
  const first = room.players[0].retainAudio;
  return room.players.every((player) => player.retainAudio === first) ? first : null;
}

function asRoom(code: string, data: Record<string, unknown> | undefined): FppRoom | null {
  if (!data) return null;
  const rawPlayers = Array.isArray(data.players) ? data.players as Array<Partial<RoomPlayer>> : [];
  return {
    code,
    createdAtMs: Number(data.createdAtMs) || 0,
    lastActivityAtMs: Number(data.lastActivityAtMs) || 0,
    status: data.status === 'play' || data.status === 'closed' || data.status === 'lobby'
      ? data.status
      : 'lobby',
    hostId: String(data.hostId || ''),
    center: Array.isArray(data.center) ? data.center.map(Number) : [],
    players: rawPlayers.map((player) => ({
      id: String(player.id || ''),
      seat: Number(player.seat) || 0,
      name: String(player.name || ''),
      pile: Array.isArray(player.pile) ? player.pile.map(Number) : [],
      guideOpen: player.guideOpen === true,
      microphoneReady: player.microphoneReady === true,
      retainAudio: typeof player.retainAudio === 'boolean' ? player.retainAudio : null,
    })),
    lastClaim: (data.lastClaim as FppRoom['lastClaim']) ?? null,
    retainAudio: typeof data.retainAudio === 'boolean' ? data.retainAudio : null,
  };
}

async function ensureGameAuth(): Promise<string> {
  const { auth: gameAuth } = await selectGameServices();
  if (gameAuth.currentUser) return gameAuth.currentUser.uid;
  throw new RoomError('auth', 'Sign in to raajadharma.org before creating or joining an online room.');
}

const API_TIMEOUT_MS = 12_000;

async function apiJson(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
  await ensureGameAuth();
  const gameAuth = getGameAuth();
  const currentUser = gameAuth.currentUser;
  if (!currentUser) throw new RoomError('auth', 'Online room authentication is unavailable.');
  const idToken = await currentUser.getIdToken();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), API_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`/api/fpp${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
        ...(init?.headers ?? {}),
      },
      body: init?.body,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new RoomError('timeout', 'The room server did not answer in time. Check the network and try again.');
    }
    throw new RoomError('unavailable', 'The room server could not be reached.');
  } finally {
    window.clearTimeout(timeoutId);
  }
  const data = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    const detail = String(data.detail || response.statusText);
    if (response.status === 404) throw new RoomError('missing', detail);
    if (response.status === 401) throw new RoomError('auth', detail);
    if (response.status === 403) throw new RoomError('not_in_room', detail);
    if (response.status === 409 && /full/i.test(detail)) throw new RoomError('full', detail);
    if (response.status === 409 && /started/i.test(detail)) throw new RoomError('started', detail);
    if (response.status === 409 && /agree|retention|choice/i.test(detail)) throw new RoomError('consent', detail);
    if (response.status === 410) throw new RoomError('closed', detail);
    if (response.status === 429) throw new RoomError('cap', detail);
    if (response.status === 400 && /match/i.test(detail)) throw new RoomError('mismatch', detail);
    if (response.status >= 500) throw new RoomError('unavailable', detail || 'Online rooms are temporarily unavailable.');
    throw new RoomError('unavailable', detail);
  }
  return data;
}

function playerBody(playerId: string, extra: Record<string, unknown> = {}): string {
  return JSON.stringify({ playerId, ...extra });
}

function roomFromResponse(data: Record<string, unknown>, playerId: string): FppRoom {
  const raw = (data.room && typeof data.room === 'object')
    ? data.room as Record<string, unknown>
    : data;
  const code = parseRoomCode(String(raw.code || data.code || '')) || '';
  if (!code) {
    throw new RoomError('unavailable', 'The server did not return a room code.');
  }
  return asRoom(code, raw) ?? {
    code,
    createdAtMs: Date.now(),
    lastActivityAtMs: Date.now(),
    status: 'lobby',
    hostId: playerId,
    center: [],
    players: [{
      id: playerId,
      seat: 1,
      name: 'P1',
      pile: [],
      guideOpen: false,
      microphoneReady: false,
      retainAudio: null,
    }],
    lastClaim: null,
    retainAudio: null,
  };
}

export async function createRoom(): Promise<{ code: string; playerId: string; room: FppRoom }> {
  const playerId = await ensureGameAuth();
  const data = await apiJson('/rooms', { method: 'POST', body: playerBody(playerId) });
  const room = roomFromResponse(data, playerId);
  return { code: room.code, playerId, room };
}

export async function joinRoom(codeRaw: string): Promise<{ playerId: string; room: FppRoom }> {
  const code = parseRoomCode(codeRaw);
  if (!code) throw new RoomError('missing', 'Invalid room code.');
  const playerId = await ensureGameAuth();
  const data = await apiJson(`/rooms/${code}/join`, { method: 'POST', body: playerBody(playerId) });
  return { playerId, room: roomFromResponse({ ...data, code }, playerId) };
}

export async function startRoom(code: string, playerId: string): Promise<void> {
  await apiJson(`/rooms/${code}/start`, { method: 'POST', body: playerBody(playerId) });
}

export async function claimRoomCard(code: string, playerId: string, word: string, centerCardId: number): Promise<'ok' | 'too_late'> {
  const data = await apiJson(`/rooms/${code}/claim`, {
    method: 'POST',
    body: playerBody(playerId, { word, centerCardId }),
  });
  return data.result === 'too_late' ? 'too_late' : 'ok';
}

export async function setGuideOpen(code: string, playerId: string, open: boolean): Promise<void> {
  await apiJson(`/rooms/${code}/guide`, {
    method: 'POST',
    body: playerBody(playerId, { open }),
  });
}

export async function setAudioRetentionChoice(code: string, playerId: string, retainAudio: boolean): Promise<void> {
  await apiJson(`/rooms/${code}/retention`, {
    method: 'POST',
    body: playerBody(playerId, { retainAudio }),
  });
}

export async function heartbeatRoom(code: string, playerId: string): Promise<boolean> {
  try {
    await apiJson(`/rooms/${code}/heartbeat`, {
      method: 'POST',
      body: playerBody(playerId),
    });
    return true;
  } catch (error) {
    if (error instanceof RoomError && (error.code === 'closed' || error.code === 'missing' || error.code === 'auth')) {
      throw error;
    }
    return false;
  }
}

export async function setMicrophoneReady(code: string, playerId: string, ready: boolean): Promise<void> {
  await apiJson(`/rooms/${code}/microphone`, {
    method: 'POST',
    body: playerBody(playerId, { ready }),
  });
}

export async function closeRoom(code: string): Promise<void> {
  const playerId = await ensureGameAuth();
  await apiJson(`/rooms/${code}/close`, { method: 'POST', body: playerBody(playerId) });
}

export async function getPeerSignal(code: string, remoteId: string): Promise<PeerSignalSnapshot> {
  const data = await apiJson(`/rooms/${code}/signals/${encodeURIComponent(remoteId)}`);
  return {
    signal: (data.signal && typeof data.signal === 'object')
      ? data.signal as PeerSignalSnapshot['signal']
      : {},
    candidates: Array.isArray(data.candidates)
      ? data.candidates as PeerSignalSnapshot['candidates']
      : [],
  };
}

export async function sendPeerSignal(
  code: string,
  remoteId: string,
  mutation: PeerSignalMutation,
): Promise<void> {
  await apiJson(`/rooms/${code}/signals/${encodeURIComponent(remoteId)}`, {
    method: 'POST',
    body: JSON.stringify(mutation),
  });
}

export function watchRoom(
  code: string,
  onRoom: (room: FppRoom | null) => void,
  onSync?: (state: RoomSyncState) => void,
): Unsubscribe {
  let stopped = false;
  let pollId: number | null = null;
  let lastEmitted = '';
  let failures = 0;
  let delayMs = 1_000;
  let sync: RoomSyncState = 'live';

  const setSync = (next: RoomSyncState) => {
    if (stopped || sync === next) return;
    sync = next;
    onSync?.(next);
  };

  const emit = (room: FppRoom | null) => {
    if (stopped || !room) return;
    const serialized = JSON.stringify(room);
    if (serialized === lastEmitted) return;
    lastEmitted = serialized;
    onRoom(room);
  };

  const schedule = () => {
    if (stopped || pollId !== null) return;
    pollId = window.setTimeout(() => {
      pollId = null;
      void tick();
    }, delayMs);
  };

  const tick = async () => {
    if (stopped) return;
    try {
      const data = await apiJson(`/rooms/${code}`);
      const raw = (data.room && typeof data.room === 'object')
        ? data.room as Record<string, unknown>
        : data;
      const room = asRoom(code, raw);
      failures = 0;
      delayMs = document.visibilityState === 'hidden' ? 4_000 : 1_000;
      setSync('live');
      emit(room);
    } catch (error) {
      if (error instanceof RoomError && (error.code === 'closed' || error.code === 'missing')) {
        setSync('ended');
        emit({
          code,
          createdAtMs: Date.now(),
          lastActivityAtMs: Date.now(),
          status: 'closed',
          hostId: '',
          center: [],
          players: [],
          lastClaim: null,
          retainAudio: null,
        });
        return;
      }
      failures += 1;
      delayMs = Math.min(8_000, 1_000 * (2 ** Math.min(failures, 3)));
      if (error instanceof RoomError && error.code === 'auth') {
        setSync('lost');
      } else if (failures >= 5) {
        setSync('lost');
      } else if (failures >= 2) {
        setSync('reconnecting');
      }
    }
    schedule();
  };

  void tick();

  const onVisibility = () => {
    if (document.visibilityState === 'visible' && !stopped) {
      delayMs = 250;
      if (pollId !== null) {
        window.clearTimeout(pollId);
        pollId = null;
      }
      void tick();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisibility);
    if (pollId !== null) window.clearTimeout(pollId);
  };
}
